import {
  Button,
  Card,
  Loader,
  TextInput,
  PhoneInput,
} from '@unmind/design-system';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Country } from 'react-phone-number-input';

import { isValidEmail } from 'Shared/Form';
import { valueIsEmpty } from 'utils';

import { useUserCurrentLocation } from 'Shared/location';
import { changeStep } from './../../lib';
import { Fieldset } from './ContactDetails.styles';
import { IContactDetailsProps } from './types';

export default ({
  dispatch,
  answers,
  details,
  errors,
  step,
}: IContactDetailsProps) => {
  const { t } = useTranslation('book', {
    keyPrefix: 'service.form',
  });
  const { data, loading } = useUserCurrentLocation();
  const [phoneIsValid, setPhoneIsValid] = useState<boolean>(false);

  const validateAnswers = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (valueIsEmpty(answers.email)) {
      dispatch({
        type: 'errors.email',
        value: t('contactDetails.email.error.required'),
      });

      return;
    } else if (!isValidEmail(answers.email)) {
      dispatch({
        type: 'errors.email',
        value: t('contactDetails.email.error.invalid'),
      });

      return;
    }

    if (valueIsEmpty(answers.phone)) {
      dispatch({
        type: 'errors.phone',
        value: t('contactDetails.phone.error.required'),
      });

      return;
    } else if (!phoneIsValid) {
      dispatch({
        type: 'errors.phone',
        value: t('contactDetails.phone.error.invalid'),
      });

      return;
    }

    changeStep({ step, dispatch });
  };

  const countryCode = (
    data?.user?.selectedCurrentLocation?.value || 'GB'
  ).toUpperCase() as Country;

  if (loading) {
    return (
      <Fieldset>
        <Loader variant="text" text={t('contactDetails.loader')} />
      </Fieldset>
    );
  }

  return (
    <Fieldset>
      <TextInput
        id="email"
        name="email"
        additionalText={{ label: t('contactDetails.email.label') }}
        type="email"
        value={answers.email}
        onChange={e =>
          dispatch([
            { type: 'answers.0.email', value: e.target.value },
            { type: 'errors.email', value: undefined },
          ])
        }
        hasError={!!errors?.email}
        errorText={errors?.email}
      />

      <Card
        element="p"
        tone="nested"
        styling="no-border"
        size="small"
        className="notes"
      >
        {t('contactDetails.email.note')}
      </Card>

      <PhoneInput
        defaultCountry={(details?.country as Country) || countryCode}
        defaultValue={details?.phoneWithoutCallingCode || ''}
        id="phone"
        error={errors?.phone || undefined}
        onChange={value => {
          setPhoneIsValid(
            typeof value.isValid !== 'undefined' ? value.isValid : true,
          );
          dispatch([
            { type: 'answers.0.phone', value: value.phone || '' },
            { type: 'errors.phone', value: undefined },
            { type: 'details.country', value: value.country || '' },
            {
              type: 'details.phoneWithoutCallingCode',
              value: value.phoneWithoutCallingCode || '',
            },
          ]);
        }}
      />

      <p className="full-width">
        <Button
          type="submit"
          label={t('ctas.start')}
          onClick={validateAnswers}
          variant="primary"
        />
      </p>
    </Fieldset>
  );
};
